import React, { useState, useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Button, Icon } from '@tencent/spaui';
import { hooks } from '@tencent/ptp-fe-common';
const { useCustomColumn } = hooks;
import { getLabels, getColumns, createColumns, updateColumns, deleteColumns, updateDefaultColumn } from '@/api/operator/cgi/smb/common';
import { getDefaultColumnsByPageId, packageCellsConfig, packageTableSelectedColumnsConfig } from '@/business-components/operator/pageColumnsConfig';
import message from '@/utils/message';
import { reportUserBehavior } from '@/pages/agency/utils/util';
import GuideTip from '@/common-components/guide-tip';
import { FLAGS } from '@/constants/Constants';
import { saveCombinedValue, getFromCombinedValue, removeFromCombinedValue } from '@/utils/storage';
import './assets/less/index.less';
import { getTableColumnConfig } from '@/api/wuji';
import commonReport from '@/common/common-report';

export interface ColumnItem {
  /**
   * 分组
   */
  group: string;
  /**
   * 列ID
   */
  name: string;
  /**
   * 列中文名
   */
  label: string;
  /**
   * 是否默认
   */
  default: boolean;
  /**
   * 是否固定
   */
  fixed: boolean;
  /**
   * 列值中文映射（JSON）
   */
  zh_cn?: string;
  /**
   * 列额外配置
   */
  config?: ColumnItemConfig;
}

export interface ColumnItemConfig {
  /**
   * 分组
   */
  group: string;
  /**
   * 列ID
   */
  name: string;
  /**
   * 列中文名
   */
  label: string;
  /**
   * 列值中文映射（JSON）
   */
  zh_cn?: string;
  /**
   * 兄弟节点
   */
  siblings?: ColumnItem[];
  /**
   * 是否隐藏
   */
  hide?: boolean;
  /**
   * 是否强制展示，无论是否选中都会出现在unfixed最前面
   * 优先级低于hide
   */
  display?: boolean;
}

/**
 * 自定列配置，同组件支持的格式
 */
export interface CustomColumnItem {
  name: string;
  list?: Array<CustomColumnItem>;
  columns?: Array<{
    name: string;
    display_name: string;
    [key: string]: any;
  }>
}

export interface CustomColumnProps {
  /**
   * 模块名
   */
  module: string;
  /**
   * 返回选择的自定义列
   */
  updateSelectedColumns?: (selectedColumns: any) => void;
  /**
   * 移除的列
   */
  removedColumns?: string[];
  /**
   * 是否使用无极配置
   */
  useWuji?: boolean;
  /**
   * 无极配置模块，默认module
   */
  wujiModule?: string;
  /**
   * 是否使用标签
   */
  haveLabel?: boolean;
  /**
   * 是否使用传入的列
   */
  customColumns?: any[];
  /**
   * 自定义列配置，参照reportService指标接口、直接append在配置里
   */
  customConfig?: CustomColumnItem[];
  /**
   * 自定义默认展示指标
   */
  customDefaultKeys?: string[];
  /**
   * 服务商UID
   */
  agency_uid?: number;
  /**
   * 列是否可编辑
   */
  editable?: false;
  /**
   * 无极列定义加载后回调
   */
  onColumnsLoaded?: (columns: Array<any>) => void;
  /**
   * 上报列字段曝光
   */
  reportFieldsExposure?: boolean;
}

// 格式转换：选择的自定义列 => 字符串
const convertConfig = (config: any) => {
  const { template_name = '', column_name_list = '' } = config;
  const [left = '', right = ''] = column_name_list.split('|');
  const unmovableCells = left.split(',') || [];
  const unfixed = right.split(',') || [];
  return {
    ...config,
    name: template_name,
    unmovableCells,
    unfixed,
    fixed: [],
  };
};

// 格式转换：字符串 => 选择的自定义列
const convertColumnNameList = (unmovableCells: string[], unfixed: string[]) => `${unmovableCells.join(',')}|${unfixed.join(',')}`;

export const CustomColumn: React.FC<CustomColumnProps> = (props) => {
  // 无极配置表格自定义列
  const [wujiColumns, setWujiColumns] = useState<any[]>([]);
  // 默认列
  const [defaultConfig, setDefaultConfig] = useState<any>({ fixed: [], unfixed: [] });
  // 已选择的列
  const [activeColumns, setActiveColumns] = useState<any[]>([]);
  // 本地自定义配置集合
  const [localConfigure, setLocalConfigure] = useState();
  // 远程自定义配置集合
  const [originConfigureList, setOriginConfigureList] = useState<any[]>([]);
  // 已拉取远端配置标志
  const [haveOriginConfig, setHaveOriginConfig] = useState(false);
  // 当前选中的自定义列配置名
  const [activeConfigureName, setActiveConfigureName] = useState('');
  // 自定义标签
  const [labelMap, setLabelMap] = useState([]);
  // 自定义标签已初始化
  const [labelInit, setLabelInit] = useState(false);
  // 全部自定义列
  const [cellsConfig, setCellsConfig] = useState({});
  // 新增字段提示
  const [columnsTipInfo, setcolumnsTipsInfo] = useState<{ key?: string, columns?: Array<string> }>({});
  const [forceDisplayColumns, setForceDisplayColumns] = useState<any[]>([]);
  // 自定义指标配置
  const [customConfigList, setCustomConfigList] = useState<any[]>([]);
  const [customConfigDefaultKeys, setCustomConfigDefaultKeys] = useState<Array<string>>([]);

  const { agency_uid, module, useWuji, wujiModule, updateSelectedColumns, removedColumns, haveLabel, customColumns = [], customConfig, customDefaultKeys, onColumnsLoaded, editable = true, reportFieldsExposure } = props;

  // 数据获取：用户自定义列配置
  useEffect(() => {
    if (!editable) {
      setHaveOriginConfig(true);
      return;
    }
    const getConfig = async () => {
      const { data: { list: columns = [] } = {} } = await getColumns({ data: { module: module.toUpperCase() } });
      const configList = columns.map((v: any) => convertConfig(v));
      const activeName = (_.find(configList, (v) => v.is_default) || {}).name || '';
      setOriginConfigureList(configList);
      // 获取当前自定义列配置
      setActiveConfigureName(activeName);
      setHaveOriginConfig(true);
    };
    getConfig();
  }, [module, editable]);

  // 数据获取：无极配置表格自定义列
  const wujiKey = wujiModule || module;
  useEffect(() => {
    if (!useWuji) {
      return;
    }
    const showTipColumns: Array<string> = [];
    const showTipLabels: Array<string> = [];
    const getConfig = async () => {
      const { data: _data = [] } = await getTableColumnConfig({ filter: `module="${wujiKey}"` });
      const data = _data.map((v: any) => {
        const column = _.pickBy(({
          ...v,
          ...(v.column || {}),
          ...JSON.parse(v.column?.config || '{}'),
          ...JSON.parse(v.config || '{}'),
        }), (x, k) => ['_id', '_ctime', '_mtime', 'column', 'config'].indexOf(k) === -1);
        column.zhCn = typeof column.zh_cn === 'string' ? JSON.parse(column.zh_cn || '{}') : column.zh_cn || {};
        // 需要提示的字段
        if (column.tip_expires && !dayjs().isAfter(dayjs(column.tip_expires), 'day')) {
          showTipColumns.push(column.name);
          showTipLabels.push(column.label);
        }
        return column;
      });

      onColumnsLoaded?.(data);
      setWujiColumns(data.filter((v: any) => !v.hide));

      // 强制展示的字段
      const forceDisplayColumns = data.filter((v: any) => !!v.display);
      setForceDisplayColumns(forceDisplayColumns);

      // 处理提示字段展示逻辑
      const tipStorageKey = getFromCombinedValue(FLAGS.GUIDE_TIP_STORAGE.GLOBAL_KEY, FLAGS.CUSTOM_COLUMN.COLUMNS_STORAGE_KEY, { defaultValue: '' });
      const [, ...oldTipColumns] = tipStorageKey.split('-');
      const hasNewColumns = _.difference(showTipColumns, oldTipColumns).length > 0;
      const tipKey = hasNewColumns ? `${FLAGS.CUSTOM_COLUMN.TIP_KEY_PREFIX}-${showTipColumns.join('-')}` : tipStorageKey;
      // 删除已过期字段
      if (hasNewColumns) {
        saveCombinedValue(FLAGS.GUIDE_TIP_STORAGE.GLOBAL_KEY, FLAGS.CUSTOM_COLUMN.COLUMNS_STORAGE_KEY, tipKey);
        if (tipStorageKey) {
          removeFromCombinedValue(FLAGS.GUIDE_TIP_STORAGE.GLOBAL_KEY, tipStorageKey);
        }
      }
      setcolumnsTipsInfo({ key: tipKey, columns: showTipLabels });
      setcolumnsTipsInfo({ key: tipKey, columns: showTipLabels });
    };
    getConfig();
  }, [useWuji, wujiKey]);

  // 自定义指标配置转换
  useEffect(() => {
    if (!Array.isArray(customConfig)) {
      return;
    }
    const customDefaultKeys: Array<string> = [];
    const convertCustomConfig = (list: Array<any>) => {
      return list?.map((v: any) => {
        const { list: cihldrenList, ...rest } = v;
        let children: Array<any> = [];
        if (cihldrenList?.length) {
          children = convertCustomConfig(cihldrenList);
        }
        return {
          ...rest,
          ...(children.length ? {
            list: children,
          } : {}),
          columns: v.columns?.filter((w: any) => !removedColumns?.includes(w.name))
            .map?.((w: any) => {
              if (w.is_default) {
                customDefaultKeys.push(w.name);
              }
              const columnName = w.displayName || w.display_name;
              return {
                ...w,
                displayName: columnName,
                label: columnName,
                default: w.is_default,
              };
            }),
        };
      });
    };
    const customConfigList = convertCustomConfig(customConfig);
    setCustomConfigList(customConfigList || []);
    setCustomConfigDefaultKeys(customDefaultKeys);
  }, [customConfig, removedColumns]);

  // 数据获取：自定义标签
  useEffect(() => {
    if (!haveLabel) {
      setLabelInit(true);
      return;
    }
    const getLabelsConfig = async () => {
      const { data: { list: labels = [] } = {} } = await getLabels();
      const labelMap = labels.map((v: any) => ({ [v.label_type]: v.label_type_name }));
      setLabelMap(labelMap);
      setLabelInit(true);
    };
    getLabelsConfig();
  }, [haveLabel]);

  // 更新：默认列
  useEffect(() => {
    if (useWuji) {
      const unmovableCells = _.filter(wujiColumns, (v) => v.default && v.fixed).map((v) => v.name);
      const unfixed = _.filter(wujiColumns, (v) => v.default && !v.fixed).map((v) => v.name);
      setDefaultConfig({
        id: '',
        template_name: '默认自定义列',
        column_name_list: convertColumnNameList(unmovableCells, unfixed),
        module: module.toUpperCase(),
        unmovableCells,
        unfixed: _.uniq([...unfixed, ...customConfigDefaultKeys, ...(customDefaultKeys || [])]),
        fixed: [],
      });
      return;
    }
    setDefaultConfig(convertConfig({
      id: '',
      template_name: '默认自定义列',
      column_name_list: getDefaultColumnsByPageId(module),
      module: module.toUpperCase(),
    }));
  }, [module, useWuji, agency_uid, wujiColumns, customConfigDefaultKeys, customDefaultKeys]);


  // 更新：全部自定义列
  useEffect(() => {
    if (useWuji) {
      const columns: any[] = [];
      // 无极配置数据
      wujiColumns.forEach((v) => {
        const item = _.find(customColumns, (x) => x.name === v.name);
        if (item) {
          const { useCustomGroup = true } = item;
          // 默认传入的覆盖已有的
          if (useCustomGroup) {
            columns.push({
              ...v,
              ...item,
            });
          } else {
            columns.push({
              ...item,
              ...v,
            });
          }
          return;
        }
        columns.push(v);
      });
      // 自定义标签数据
      labelMap.forEach((v) => {
        const name = _.keys(v)[0];
        columns.push({
          name,
          label: v[name],
          group: '标签指标',
          isLabel: true,
        });
      });
      // 自定义传入数据
      customColumns.forEach((v) => {
        const item = _.find(columns, (x) => x.name === v.name);
        if (item) {
          return;
        }
        columns.push(v);
      });
      const coulmnGroup = _.groupBy(_.filter(columns, (v) => !_.find(removedColumns, (x) => x === v.name)), 'group');
      const list = _.keys(coulmnGroup).map((v: string) => {
        return {
          name: v,
          columns: coulmnGroup[v].map((x) => ({
            ...x,
            displayName: x.label,
            cellName: x.label,
            align: 'left',
          })),
        };
      });
      setCellsConfig({
        self_catogories: {
          list: [...list, ...customConfigList || []],
          name: '自定义指标',
        },
      });
      return;
    }
    const config = packageCellsConfig(module, labelMap, '|', removedColumns || [], haveLabel, {}, customColumns);
    setCellsConfig(config);
  }, [module, useWuji, wujiColumns, removedColumns, labelMap, customColumns, customConfigList]);

  // 更新：远程自定义配置
  const updateOriginConfigureList = useCallback((newConfigureList) => {
    const deleteList = _.difference(originConfigureList, newConfigureList);
    const addList = _.difference(newConfigureList, originConfigureList);

    // 新增
    if (addList.length === 1 && deleteList.length === 0) {
      const config = addList[0] || {};
      const { name: template_name, unmovableCells = [], unfixed = [] } = config;

      if (!template_name) {
        return;
      }

      const params = {
        module: module.toUpperCase(),
        template_name,
        column_name_list: convertColumnNameList(unmovableCells, unfixed),
      };

      const getConfig = async () => {
        // 新增
        const { code } = await createColumns({ data: params });
        if (code !== 0) {
          return;
        }
        message.success(`新增自定义列“${template_name}”成功`);
        // 获取新列表
        const {
          data: { list: columns = [] },
        } = await getColumns({ data: { module: module.toUpperCase() } }, false, false);
        const configList = columns.map((v: any) => convertConfig(v));
        const { template_name: activeName = '', id = '' } = _.find(configList, (v) => v.template_name === template_name) || {};
        setOriginConfigureList(configList);
        setActiveConfigureName(activeName);
        // 更新选中
        updateDefaultColumn({ data: { module, id, is_default: 1 } });
      };
      getConfig();

      return;
    }

    // 修改
    if (addList.length === 1 && deleteList.length === 1) {
      setOriginConfigureList(newConfigureList);
      const config = addList[0] || {};
      const { id, name: template_name, unmovableCells = [], unfixed = [] } = config;
      const params = {
        module: module.toUpperCase(),
        id,
        template_name,
        column_name_list: convertColumnNameList(unmovableCells, unfixed),
      };
      updateColumns({ data: params });
      message.success(`修改自定义列“${template_name}”成功`);
      return;
    }

    // 删除
    if (addList.length === 0 && deleteList.length === 1) {
      setOriginConfigureList(newConfigureList);
      const config = deleteList[0] || {};
      const { id, name: template_name } = config;
      const params = {
        module: module.toUpperCase(),
        id,
      };
      deleteColumns({ data: params });
      message.success(`删除自定义列“${template_name}”成功`);
      return;
    }
  }, [module, originConfigureList]);

  // 更新：当前选中的自定义列配置
  const updateActiveConfigureName = useCallback((name) => {
    if (name === 'SESSION_STORAGE') {
      setActiveConfigureName(name);
      return;
    }

    const hasName = _.find(originConfigureList, (v: any) => v.name === name);
    if (!hasName) {
      return;
    }

    setActiveConfigureName(name);
    if (name) {
      message.success(`自定义列“${name}”应用成功`);
    }

    const id = (_.find(originConfigureList, (v: any) => v.name === name) || {}).id;
    if (id && name !== activeConfigureName) {
      const params = {
        module: module.toUpperCase(),
        id,
        is_default: 1,
      };
      updateDefaultColumn({ data: params });
    }
  }, [activeConfigureName, originConfigureList]);

  // 上报自定义列变更
  const reportCustomColumnChange = useCallback((oldColumns = [], newColumns = []) => {
    const nameMap: any = {};
    const oldColumnsNameList = oldColumns.map((v: any) => {
      nameMap[v.name] = v;
      return v.name;
    });
    const newColumnsNameList = newColumns.map((v: any) => {
      nameMap[v.name] = v;
      return v.name;
    });

    const decrease = _.difference(oldColumnsNameList, newColumnsNameList);
    const increase = _.difference(newColumnsNameList, oldColumnsNameList);

    if (increase.length > 0) {
      reportUserBehavior({
        behaviorType: '增加自定义列指标',
        relation_id: increase.join(','),
        relation_name: increase.map((v: any) => nameMap[v].label).join(','),
        relation_type: '自定义列',
      });
    }

    if (decrease.length > 0) {
      reportUserBehavior({
        behaviorType: '减少自定义列指标',
        relation_id: decrease.join(','),
        relation_name: decrease.map((v: any) => nameMap[v].label).join(','),
        relation_type: '自定义列',
      });
    }
  }, []);

  const customColumnParams = useMemo(() => {
    return {
      // 自定义筛选项数据
      configureDataSource: {
        localConfigure,
        updateLocalConfigure: (v: any) => setLocalConfigure(v),
        originConfigureList,
        updateOriginConfigureList,
        activeConfigureName,
        updateActiveConfigureName,
      },
      columnsDefs: {
        cellsConfig,
        dropMenuTriggerProps: {
          // alignment: 'right',
        },
        dropMenuTriggerComponent: <Button
          className="borderless"
          icon={<Icon name="custom-outlined" />}
          data-report="custom-column"
        >
          自定义列
        </Button>,
      },
      // 默认值
      defaultSelectedColumns: defaultConfig,
    };
  }, [localConfigure, originConfigureList, updateOriginConfigureList, activeConfigureName, updateActiveConfigureName, cellsConfig, defaultConfig]);

  const { selectedColumns, customColumnComponent } = useCustomColumn(customColumnParams);

  // 返回：选择的自定义列
  useEffect(() => {
    if (updateSelectedColumns && haveOriginConfig && !_.isEmpty(cellsConfig)) {
      const config = selectedColumns || {};
      const { unmovableCells = [], unfixed = [] } = config;
      const getTableColumns = () => {
        if (useWuji) {
          const columns: any[] = [];
          const list = _.get(cellsConfig, 'self_catogories.list') || [];
          // 获取嵌套的列
          const getColumns = (list: Array<any>) => {
            return list.map((v: any) => {
              let children: Array<any> = [];
              if (v.list?.length) {
                children = getColumns(v.list).flat();
              }
              return [...v.columns || [], ...children];
            }).flat();
          };
          const allColumns = getColumns(list);
          unmovableCells.forEach((v) => {
            const item = _.find(allColumns, (x: any) => x.name === v);
            if (item) {
              columns.push({
                ...item,
                fixedable: true,
              });
              if (item.siblings) {
                columns.push(...item.siblings);
              }
            }
          });
          unfixed.forEach((v) => {
            const item = _.find(allColumns, (x: any) => x.name === v);
            if (item) {
              columns.push({
                ...item,
              });
              if (item.siblings) {
                columns.push(...item.siblings);
              }
            }
          });
          return columns;
        }
        return packageTableSelectedColumnsConfig(module, labelMap, convertColumnNameList(unmovableCells, unfixed), {}, customColumns);
      };
      let columns = getTableColumns();
      columns = columns.filter(({ name }) => !removedColumns?.includes(name));
      // 有强制展示的字段
      if (forceDisplayColumns.length) {
        const filteredForceDisplayColumns = forceDisplayColumns.filter(({ name }) => !removedColumns?.includes(name));
        if (filteredForceDisplayColumns.length) {
          const currentColumnKeys = columns.map(({ name }) => name);
          const fixedLength = _.intersection(unmovableCells || [], currentColumnKeys).length || 0;
          columns.splice(fixedLength, 0, ...filteredForceDisplayColumns);
          columns = _.unionBy(columns, 'name');
        }
      }
      if (!_.isEqual(activeColumns, columns) && labelInit) {
        if (activeColumns.length > 0) {
          reportCustomColumnChange(activeColumns, columns);
        }
        setActiveColumns(columns);
        // 曝光字段上报
        if (reportFieldsExposure) {
          commonReport.reportAction({
            action: '_view-column-fields', // _view开头表示全局曝光字段
            relation_key: module,
            relation_value: columns.map((v: any) => `${v.name},${v.label}`).join(';'),
            extra_data: {
              label: '表格展示字段',
            },
          });
        }
        updateSelectedColumns({ ...config, columns });
      }
    }
  }, [module, useWuji, cellsConfig, labelMap, selectedColumns, customColumns, customConfigList, labelInit, haveOriginConfig, reportFieldsExposure]);

  // 列不可编辑时，隐藏自定义列入口
  if (!editable) {
    return null;
  }

  return columnsTipInfo.columns && columnsTipInfo.columns.length > 0
    ? <GuideTip
      markKey={columnsTipInfo.key || ''}
      title={'自定义列'}
      content={<div>自定义列新增{`"${columnsTipInfo.columns?.join('", "')}"`}字段</div>}
      placement={'bottom'}
    >
      <div>{customColumnComponent}</div>
    </GuideTip>
    : customColumnComponent;
};

CustomColumn.defaultProps = {
  removedColumns: [],
  useWuji: false,
  haveLabel: true,
  customColumns: [],
};

export default CustomColumn;
